<template>
  <div id="mapContainer" class="kpt-map-canvas"></div>
</template>
<script setup>
import { 
  defineProps, 
  reactive, 
  watch,
  computed,
  ref,
  onMounted,
} from 'vue';
import mapboxgl from 'mapbox-gl';
import { useRouter } from 'vue-router/composables';

const router = useRouter();
const props = defineProps(['terminals', 'terminalsLoading', 'onlineOnly']);
let map = reactive({});
const features = ref([]);
const maptimeout = ref(null);
const filteredTerminals = computed(() => {
  if (props.onlineOnly.value) {
    return props.terminals.filter(r => (r.networkStatus?.satelliteOnline || r.networkStatus?.cellularOnline) 
      && r.location?.coordinates?.latitude 
      && r.location?.coordinates?.longitude);
  }
  return props.terminals.filter(r => r.location?.coordinates?.latitude && r.location?.coordinates?.longitude);
});
// Clears the source and rendered layer from the map
const clearSourceAndLayer = () => {
  // remove the layer if it exists
  if (map.getLayer('clusters')) {
    map.removeLayer('clusters');
  }
  if (map.getLayer('unclustered-point')) {
    map.removeLayer('unclustered-point');
  }
  if (map.getLayer('cluster-count')) {
    map.removeLayer('cluster-count');
  }
  // then remove the source
  if (map.getSource('remotes')) {
    map.removeSource('remotes');
  }
}
const tl = computed(() => props.terminalsLoading);
const oo = computed(() => props.onlineOnly);

const seedMap = async () => {
  if (!map || !map.loaded()) {
    maptimeout.value = setTimeout(() => { // retry loading map after one second
      seedMap();
    }, 1000);
    return;
  }
  features.value = [];
  filteredTerminals.value.forEach(terminal => {
    if (terminal.private || !terminal.location?.coordinates || !terminal.networkStatus) return;
    // determine which lat/long to use (cell vs sat) 
    const coords = [terminal.location.coordinates.longitude, terminal.location.coordinates.latitude]
    const terminalToPush = { 
      type: 'Feature', 
      geometry: { 
        type: 'Point', 
        coordinates: coords
      }, 
      properties: { 
        id: terminal.id, 
        name: terminal.name, 
        serial: terminal.serial,
        name_lc: terminal.name.toLowerCase(), 
        healthValue: terminal.networkStatus.satelliteOnline, 
        color: terminal.networkStatus.satelliteOnline
          ? '#65BD81'
          : '#FF5959'
      } 
    };
    features.value.push(terminalToPush);
  });

  // 1. Add source first
  map.addSource('terminals', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: features.value
    },
    cluster: true,
    clusterMaxZoom: 14, // Max zoom to cluster points on
    clusterRadius: 50 // Radius of each cluster when clustering points (defaults to 50)
  });

  // 2. Add the clusters
  map.addLayer({
    id: 'clusters',
    type: 'circle',
    source: 'terminals',
    filter: ['has', 'point_count'],
    paint: {
      // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
      // with three steps to implement three types of circles:
      //   * Blue, 20px circles when point count is less than 100
      //   * Yellow, 30px circles when point count is between 100 and 750
      //   * Pink, 40px circles when point count is greater than or equal to 750
      'circle-color': [
        'step',
        ['get', 'point_count'],
        '#51bbd6',
        100,
        '#f1f075',
        750,
        '#f28cb1'
      ],
      'circle-radius': [
        'step',
        ['get', 'point_count'],
        20,
        100,
        30,
        750,
        40
      ]
    }
  });

  // 3. Add the cluster count on top of the cluster circles
  map.addLayer({
    id: 'cluster-count',
    type: 'symbol',
    source: 'terminals',
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['Arial Unicode MS Bold'],
      'text-size': 12
    }
  });
  
  // 4. Add all unclustered points (individual remotes)
  map.addLayer({
    id: 'unclustered-point',
    type: 'circle',
    source: 'terminals',
    filter: ['!', ['has', 'point_count']],
    paint: {
      // eslint-disable-next-line quote-props
      'circle-color': { 'type': 'identity', 'property': 'color' },
      'circle-radius': 5,
      'circle-stroke-width': 1,
      'circle-stroke-color': '#FFFFFF'
    }
  });

  // 5. Add a popup placeholder
  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false
  });

  // #region Click bindings
  map.on('click', 'clusters', (e) => {
    const f = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
    const clusterId = f[0].properties.cluster_id;
    map.getSource('remotes').getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;
      map.easeTo({
        center: features[0].geometry.coordinates,
        zoom
      });
    });
  });

  map.on('click', 'unclustered-point', (e) => {
    const coordinates = e.features[0].geometry.coordinates.slice();
    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    router.push({ path: `/terminals/${e.features[0].properties.id}/overview` });
  });
  // #endregion

  // #region Mouseover bindings
  map.on('mouseenter', 'clusters', () => {
    map.getCanvas().style.cursor = 'pointer';
  });
  map.on('mouseleave', 'clusters', () => {
    map.getCanvas().style.cursor = '';
  });
  map.on('mouseenter', 'unclustered-point', (e) => {
    map.getCanvas().style.cursor = 'pointer';

    // Populate the popup and set its coordinates
    // based on the feature found.
    const terminalName = e.features[0].properties.name;
    const serial = e.features[0].properties.serial;
    const status = e.features[0].properties.healthValue;
    let popupString = `<div class="map-popup">
                <h5><strong>${terminalName}</strong></h5>
                <h6>Serial: ${serial || 'Not set'}</h6>`;
    if ((status) && status != 'null') popupString += `<h6>Status: ${status ? 'Online' : 'Offline'}</h6>`;
    popupString += '</div>';

    popup
      .setLngLat(e.features[0].geometry.coordinates)
      .setHTML(popupString)
      .addTo(map);
  });
  map.on('mouseleave', 'unclustered-point', () => {
    map.getCanvas().style.cursor = '';
    popup.remove();
  });
  // #endregion

  clearTimeout(maptimeout.value);
}

watch(tl, (newvalue) => {
  if (newvalue === false) {
    seedMap();
  }
});
watch(oo, () => {
  clearSourceAndLayer();
  seedMap();
});
onMounted(async () => {
  mapboxgl.accessToken = 'pk.eyJ1IjoiY2xvdWRzZXJ2aWNlczNwcCIsImEiOiJjajg1MmF4c2wwZzV6MnFxYnM5dDZ3NG9qIn0.CW3uCZJi87UiXEUGyR21IQ';

  maptimeout.value = setTimeout(() => {
    // instantiate the map
    map = new mapboxgl.Map({
      container: 'mapContainer',
      // style: 'mapbox://styles/mapbox/light-v9',
      style: 'mapbox://styles/cloudservices3pp/clgckhays001x01qe5a20do8r',
      center: [0, 0], // offset a bit to show more civilization
      zoom: 1
    });
    map.addControl(new mapboxgl.FullscreenControl());
    map.addControl(new mapboxgl.NavigationControl());
    // map.setRenderWorldCopies(false);
  }, 50);
});
</script>
<style lang="scss" scoped>
.kpt-map-canvas {
  height: 100%;
  width: 100%;
  position: relative;

  .card-content {
    position: relative;
    overflow-y: auto;

    .map-control {
      position: absolute;
      top: 1px;
      left: 0;
      z-index: 9;
      width: 100%;
      height: 100%;

      pre {
        width: 100%;
      }
    }
  }
}
</style>
